import { beginLogin, clearSession, getAccessToken, isTokenExpiringSoon, refreshTokens } from './oidc';

/**
 * API Configuration for frontend services
 */

// API base URL configuration
export const API_CONFIG = {
  // Base URL for API requests - injected at build time via VITE_API_BASE_URL, falls back to relative path for local dev (Vite proxy)
  baseUrl: import.meta.env.VITE_API_BASE_URL || '/api',
  
  // API endpoints
  endpoints: {
    // Capsifi API
    capsifiValueStreams: '/capsifi/valuestreams',
    capsifiValueStreamDetails: '/capsifi/valuestream',
    capsifiCapabilities: '/capsifi/capabilities',
    capsifiOutcomes: '/capsifi/outcomes',
    
    metricsTimeseriesBlob: '/metrics/salesforce/sales/timeseries-blob',

    // Prismatic API (alternative data source)
    prismaticTimeseries: '/prismatic/timeseries',
    prismaticSummary: '/prismatic/summary',

    // Copilot Chat API
    copilotChats: '/copilot/chats'
  },
  
  // Header keys
  headers: {
    contentType: 'application/json',
    modelId: 'X-Active-Model'              // Used for Capsifi API
  }
};

/**
 * Get headers for Prismatic API requests
 */
export const getPrismaticHeaders = (apiKey: string, apiUrl: string) => {
  return {
    'Content-Type': API_CONFIG.headers.contentType,
    'API-Key': apiKey,
    'X-Prismatic-Api-Url': apiUrl
  };
};

/**
 * Helper to handle API responses and error handling
 */
export const handleApiResponse = async <T>(response: Response): Promise<T> => {
  if (response.status === 401) {
    // Bearer-authenticated requests go through apiFetch, which already owns the
    // refresh/re-login lifecycle. A 401 that reaches here is therefore either a
    // non-bearer request (e.g. the Prismatic API-key path, where 401 means a bad
    // key) or an already-refreshed request that is still unauthorized. In neither
    // case does re-login help — redirecting would just loop — so surface an error.
    throw new Error('Not authorized to access this resource.');
  }

  if (!response.ok) {
    let errorMessage = `Request failed with status ${response.status}`;
    
    try {
      const errorData = await response.json();
      errorMessage = errorData.error || errorData.message || errorMessage;
    } catch (e) {
      // If we can't parse the error response, use the default message
    }
    
    throw new Error(errorMessage);
  }

  return response.json() as Promise<T>;
};

/**
 * Fetch wrapper that attaches a Bearer token and handles proactive token refresh.
 * Falls back to a plain fetch when no token is present (unauthenticated requests).
 *
 * Refreshes are single-flighted: concurrent callers that hit an expiring token
 * share one refresh promise instead of each triggering a silent renew. On a 401
 * the request is retried once with a freshly refreshed token before giving up.
 */
let inflightRefresh: Promise<boolean> | null = null;

function refreshOnce(): Promise<boolean> {
  if (!inflightRefresh) {
    inflightRefresh = refreshTokens().finally(() => {
      inflightRefresh = null;
    });
  }

  return inflightRefresh;
}

async function sendWithBearer(input: RequestInfo | URL, init: RequestInit): Promise<Response> {
  const token = await getAccessToken();
  const headers = new Headers(init.headers);

  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  return fetch(input, { ...init, headers });
}

export async function apiFetch(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
  if (await isTokenExpiringSoon()) {
    await refreshOnce();
  }

  let response = await sendWithBearer(input, init);

  if (response.status === 401) {
    const refreshed = await refreshOnce();

    if (!refreshed) {
      // Refresh failed — the session is genuinely gone. Re-login.
      void clearSession();
      void beginLogin();
      throw new Error('Session expired — redirecting to login');
    }

    response = await sendWithBearer(input, init);

    // Still 401 after a fresh token: this is an authorization problem for this
    // specific request (e.g. the backend rejects the token's azp/audience), not
    // an expired session. Re-login would just bounce through Keycloak and land
    // back here in a redirect loop, so surface the error instead.
    if (response.status === 401) {
      throw new Error('Not authorized to access this resource.');
    }
  }

  return response;
}
